import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";
import {
  CatalogEntityType,
  CatalogMeta,
  CatalogTabFilters,
  FieldValue,
  TabKey,
} from "../types";

const VALID_TABS: TabKey[] = ["all", "activities", "courses", "paths"];

const isValidTab = (value: string | null): value is TabKey =>
  !!value && (VALID_TABS as string[]).includes(value);

const EMPTY_FIELD_FILTERS: Record<string, FieldValue> = {};
const EMPTY_META: CatalogMeta = {};

const TAB_TO_ENTITY: Record<Exclude<TabKey, "all">, CatalogEntityType> = {
  activities: "activity",
  courses: "course",
  paths: "path",
};

export interface CatalogPageState {
  activeTab: TabKey;

  entityType: CatalogEntityType;

  search: string;
  fieldFilters: Record<string, FieldValue>;
  sort: string | null;
  page: number;

  setActiveTab: (next: TabKey) => void;

  update: (patch: Partial<CatalogTabFilters>) => void;
}

export const useCatalogPageState = (): CatalogPageState => {
  const { user } = useAuth();
  const { filters: contextFilters, setFilters } = useFilters();
  const [searchParams, setSearchParams] = useSearchParams();

  const userScope = user?.id_user ?? "anon";

  const metaFilterId = `${userScope}_v2_catalog_meta`;
  const meta = (contextFilters[metaFilterId] || EMPTY_META) as CatalogMeta;
  const persistedTab: TabKey = isValidTab(String(meta.activeTab ?? ""))
    ? (meta.activeTab as TabKey)
    : "activities";

  const urlTabParam = searchParams.get("tab");
  const urlTab: TabKey | null = isValidTab(urlTabParam) ? urlTabParam : null;
  const activeTab: TabKey = urlTab ?? persistedTab;

  useEffect(() => {
    if (urlTab && urlTab !== persistedTab) {
      setFilters(metaFilterId, { ...meta, activeTab: urlTab });
    }
  }, [urlTab, persistedTab, meta, metaFilterId, setFilters]);

  const entityType: CatalogEntityType =
    activeTab === "all" ? "activity" : TAB_TO_ENTITY[activeTab];

  const filterId = `${userScope}_v2_catalog_${activeTab}`;
  const current = (contextFilters[filterId] || {}) as CatalogTabFilters;

  const search = current.search ?? "";
  const fieldFilters = current.fieldFilters ?? EMPTY_FIELD_FILTERS;
  const sort = current.sort ?? "most-recent";
  const page = current.page ?? 1;

  const setActiveTab = (next: TabKey) => {
    setFilters(metaFilterId, { ...meta, activeTab: next });
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", next);
    setSearchParams(nextParams, { replace: true });
  };

  const update = (patch: Partial<CatalogTabFilters>) => {
    setFilters(filterId, { ...current, ...patch });
  };

  return {
    activeTab,
    entityType,
    search,
    fieldFilters,
    sort,
    page,
    setActiveTab,
    update,
  };
};
